<template>
  <section
    class="grid grid-cols-[2.125rem_1fr] lg:grid-cols-1 lg:grid-rows-[5rem_max-content] gap-1.5 lg:gap-0 lg:w-full transition-[gap]"
    :style="`--phase-color: ${phase.color};}`"
  >
    <div
      class="flex flex-col items-center justify-center lg:relative lg:w-full"
    >
      <span
        class="w-full lg:w-20 aspect-square rounded-full bg-(--phase-color) flex items-center justify-center text-white font-bold text-lg lg:text-4xl lg:absolute lg:inset-0 lg:m-auto"
        >{{ index }}</span
      >
      <span
        class="block w-1.5 lg:w-full bg-(--phase-color) h-full lg:h-2.5"
      ></span>
    </div>
    <div class="lg:grid lg:grid-cols-2 lg:gap-50 lg:-translate-y-17.5">
      <div :class="{ 'lg:order-2': index % 2 > 0 }">
        <h2>
          <button
            :id="`phases.${phase.key}.button`"
            class="h-8.5 uppercase font-bold lg:h-auto w-max max-w-full flex items-center bg-(--phase-color) focus:bg-white hover:bg-white text-white focus:text-(--phase-color) hover:text-(--phase-color) border-2 border-transparent focus:border-(--phase-color) hover:border-(--phase-color) py-1 lg:py-2 px-2.5 lg:px-5 rounded-sm lg:rounded-lg text-lg lg:text-3xl relative cursor-pointer transition-[background_color-color] group"
            @click.stop="
              updateToolTip(
                $event,
                `phases.${phase.key}`,
                index % 2 === 0,
                true,
                `phases.${phase.key}.button`
              )
            "
            @touchstart.stop="
              updateToolTip(
                $event,
                `phases.${phase.key}`,
                index % 2 === 0,
                true,
                `phases.${phase.key}.button`
              )
            "
            @mouseenter.stop="
              updateToolTip($event, `phases.${phase.key}`, index % 2 === 0)
            "
            @mouseleave.stop="removeToolTip"
          >
            <span>{{ t(`phases.${phase.key}.title`) }}</span>
            <span
              class="w-5.5 aspect-square rounded-full flex items-center justify-center bg-white/20 group-hover:bg-(--phase-color) group-focus:bg-(--phase-color) ml-2.5 shrink-0 transition-[background_color]"
            >
              <IconSprite
                id="plus"
                role="presentation"
                class="text-[8px] group-hover:text-white group-focus:text-white transition-[color]"
              />
            </span>
            <span
              class="hidden lg:block w-0 h-0 border-l-20 border-r-20 border-transparent border-t-(--phase-color) border-t-20 absolute bottom-0 left-8 translate-y-4 -z-10"
            ></span>
          </button>
        </h2>
        <div
          :class="[
            'flex items-center gap-x-2.5 gap-y-5 lg:gap-7.5 mt-5 lg:mt-11.25 transition-[margin_gap]',
            { 'flex-wrap': phase.wrap },
          ]"
        >
          <img
            :src="images[phase.key]"
            :alt="t(`alt.phases.${phase.key}`)"
            class="h-23 lg:h-45 transition-[height]"
          />
          <p class="text-grey font-bold text-lg lg:text-xl">
            {{ t(`phases.${phase.key}.questions`) }}
          </p>
        </div>
      </div>
      <div>
        <div
          :class="[
            'flex flex-wrap gap-2.5 lg:gap-5 mt-7.5 lg:mt-0 transition-[margin_gap]',
            { 'lg:justify-end': index % 2 > 0 },
          ]"
        >
          <div
            v-for="i in phase.personas"
            class="w-12.5 lg:w-15 aspect-square rounded-full flex items-center justify-center bg-(--phase-color) text-white"
            :title="t(`personas.${i}.title`)"
          >
            <IconSprite
              :id="`personas_${i}`"
              class="text-[26px] lg:text-[32px]"
              role="presentation"
            />
          </div>
        </div>
        <div
          class="mt-7.5 lg:mt-11.25 mb-12.5 flex flex-col lg:grid lg:grid-cols-2 gap-5 lg:gap-10 transition-[gap_margin]"
        >
          <div v-for="type in ['actions', 'principles']" class="lg:relative">
            <h3 class="text-base text-(--phase-color) font-bold uppercase">
              {{ t(`labels.${type}`) }}
            </h3>
            <div
              v-for="(item, itemIndex) in phase[type as keyof Phase]"
              class="flex gap-2.5 mt-2.5 text-grey text-left cursor-pointer"
            >
              <span class="w-5">{{
                type === 'actions' ? `${index}.${itemIndex + 1}` : `${item}.`
              }}</span>
              <button
                :id="`${phase.key}${type}.${item}`"
                class="block text-left cursor-pointer group"
                @touchstart.stop="
                  updateToolTip(
                    $event,
                    `${type}.${item}`,
                    index % 2 > 0,
                    true,
                    `${phase.key}${type}.${item}`
                  )
                "
                @click.stop="
                  updateToolTip(
                    $event,
                    `${type}.${item}`,
                    index % 2 > 0,
                    true,
                    `${phase.key}${type}.${item}`
                  )
                "
                @mouseenter.stop="
                  updateToolTip($event, `${type}.${item}`, index % 2 > 0)
                "
                @mouseleave.stop="removeToolTip"
              >
                <PhaseCardItemContent
                  :active="tooltip?.id === `${type}.${item}`"
                  :text="t(`${type}.${item as string}.title`)"
                />
              </button>
            </div>
            <hr
              v-if="type === 'actions'"
              class="border-dashed border-t lg:border-t-0 border-light-grey mt-5 lg:mt-0 w-full lg:border-r lg:h-full lg:absolute lg:top-0 lg:-right-5 pointer-events-none transition-[margin]"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import images from '@/assets/phases/index.ts'
import IconSprite from '@/components/small/IconSprite.vue'
import PhaseCardItemContent from '@/components/small/PhaseCardItemContent.vue'
import { useToolTipControls } from '@/composables/useToolTipControls.ts'
import { toRef } from 'vue'

const { index, phase, tooltip } = defineProps<{
  index: number
  phase: Phase
  tooltip: ToolTip | null
}>()

const { t } = useI18n()

const emit = defineEmits(['update:tooltip', 'remove:tooltip'])

const { updateToolTip, removeToolTip } = useToolTipControls(
  emit,
  toRef(() => tooltip)
)
</script>
